import { ThemeToggle } from "@/components/motion/theme-toggle";
import { BrandLogo } from "@/components/brand-logo";
import { Button, StatefulButton } from "@/components/motion/button";
import { ButtonRouterLink } from "@/components/motion/button/router-link";
import { Input } from "@/components/motion/input";
import { Modal, ModalContent } from "@/components/motion/modal";
import { Drawer } from "@/components/motion/drawer";
import {
  MorphPopover,
  MorphPopoverContent,
  MorphPopoverTrigger,
} from "@/components/motion/popover-morph";
import { Tooltip } from "@/components/motion/tooltip";
import { useClerk, useUser } from "@clerk/tanstack-react-start";
import {
  // Bot, // Agent access link, commented out below
  LayoutGrid,
  LogOut,
  Mail,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  Search,
  Settings,
  X,
  type LucideIcon,
} from "lucide-react";
import { Link, ScriptOnce, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";

import { parseInboxDirection, type InboxDirection } from "@/features/inbox/filters";
import { PALETTE_CSS } from "@/lib/palettes";
import {
  SIDEBAR_INIT_SCRIPT,
  applySidebarPinned,
  readSidebarPinned,
  writeSidebarPinned,
} from "@/lib/sidebar-state";
import { getErrorMessage } from "@/lib/errors";
import { captureHandledError, clearUser, logWarn } from "@/lib/glitchtip";
import { capturePostHogEvent, resetPostHog } from "@/lib/posthog-client";
import { cn } from "@/lib/utils";
import { DASHBOARD_FEATURES, DASHBOARD_FOLDERS, EmptyStateCard, getInitials } from "./shared";

const INBOX_PATH = "/dashboard/inbox";
const COMPOSE_PATH = "/dashboard/compose";

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function isMailPath(pathname: string) {
  return isActivePath(pathname, INBOX_PATH) || isActivePath(pathname, COMPOSE_PATH);
}

type MailSearch = {
  domainId: string | undefined;
  aliasId: string | undefined;
  direction: InboxDirection;
  q: string | undefined;
  includeBounce: true | undefined;
  includeWarmup: true | undefined;
};

/** The mailbox filters carried in the URL, so shell links keep the current view. */
function readMailSearch(search: Record<string, unknown>): MailSearch {
  return {
    domainId: typeof search.domainId === "string" ? search.domainId : undefined,
    aliasId: typeof search.aliasId === "string" ? search.aliasId : undefined,
    direction: parseInboxDirection(search.direction),
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    includeBounce: search.includeBounce === true ? true : undefined,
    includeWarmup: search.includeWarmup === true ? true : undefined,
  };
}

function useMailLocation() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const search = useRouterState({
    select: (state) => state.location.search as Record<string, unknown>,
  });
  const onMail = isMailPath(pathname);
  // Filters only mean something on mail routes; elsewhere links start clean.
  const mail = readMailSearch(onMail ? search : {});
  return { pathname, onMail, mail };
}

type IdleHandle = number | ReturnType<typeof setTimeout>;

function scheduleIdleWork(callback: () => void): IdleHandle {
  const browserWindow = globalThis as typeof globalThis & {
    requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };

  if (typeof browserWindow.requestIdleCallback === "function") {
    const requestIdleCallback = browserWindow.requestIdleCallback;
    return requestIdleCallback(callback, { timeout: 1200 });
  }

  return globalThis.setTimeout(callback, 250);
}

function cancelIdleWork(handle: IdleHandle) {
  const browserWindow = globalThis as typeof globalThis & {
    cancelIdleCallback?: (id: number) => void;
  };

  if (typeof browserWindow.cancelIdleCallback === "function") {
    const cancelIdleCallback = browserWindow.cancelIdleCallback;
    cancelIdleCallback(handle as number);
    return;
  }

  globalThis.clearTimeout(handle);
}

export function DashboardShell({ children }: { children: React.ReactNode }) {
  const clerk = useClerk();
  const router = useRouter();
  const { pathname } = useMailLocation();
  const [sidebarPinned, setSidebarPinned] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  // The dialog keeps showing the last message while it folds away.
  const [lastSignOutError, setLastSignOutError] = useState("");
  if (signOutError && signOutError !== lastSignOutError) {
    setLastSignOutError(signOutError);
  }
  const warmedRoutesRef = useRef(false);

  const toggleSidebarPin = useCallback(() => {
    setSidebarPinned((current) => {
      writeSidebarPinned(!current);
      return !current;
    });
  }, []);

  // Layout effect, so arriving here by client-side navigation (where the inline
  // script below does not run) is also settled before the browser paints.
  useLayoutEffect(() => {
    const pinned = readSidebarPinned();
    applySidebarPinned(pinned);
    setSidebarPinned(pinned);
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Keyboard shortcut: Ctrl+[ to toggle the sidebar
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "[" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebarPin();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebarPin]);

  useEffect(() => {
    if (warmedRoutesRef.current) return;
    warmedRoutesRef.current = true;

    const slowConnection =
      typeof navigator !== "undefined" &&
      "connection" in navigator &&
      Boolean(
        (
          navigator as Navigator & {
            connection?: { saveData?: boolean; effectiveType?: string };
          }
        ).connection?.saveData,
      );

    if (slowConnection) {
      return;
    }

    const targets = [INBOX_PATH, ...DASHBOARD_FEATURES.map((item) => item.to)].filter(
      (to) => !isActivePath(pathname, to),
    );

    let cancelled = false;
    const handle = scheduleIdleWork(() => {
      void (async () => {
        for (const [index, to] of targets.entries()) {
          if (cancelled) return;

          try {
            await router.preloadRoute({ to });
          } catch {
            // Best-effort warming only.
          }

          if (index < targets.length - 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 120));
          }
        }
      })();
    });

    return () => {
      cancelled = true;
      cancelIdleWork(handle);
    };
  }, [pathname, router]);

  const handleSignOut = async () => {
    setSignOutError(null);
    setIsSigningOut(true);

    try {
      await clerk.signOut({ redirectUrl: "/login" });
      capturePostHogEvent("signed_out");
      resetPostHog();
      void clearUser();
    } catch (error) {
      void captureHandledError({
        error,
        options: {
          tags: {
            area: "sign_out",
            surface: "browser",
          },
        },
      });
      void logWarn({
        message: "Clerk sign-out failed.",
        tags: {
          area: "sign_out",
          surface: "browser",
        },
      });
      setSignOutError(
        getErrorMessage(
          error,
          "Sign-out did not complete. Your protected session is still active.",
        ),
      );
      // One overlay at a time: the blocking dialog replaces the mobile drawer.
      setMobileNavOpen(false);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className="hb-shell hb-dashboard-shell font-hb relative flex h-dvh flex-col overflow-hidden bg-hb-sidebar text-hb-text selection:bg-hb-accent selection:text-hb-accent-fg">
      {/* Collapsed or expanded is decided here, while the HTML is still parsing. */}
      <ScriptOnce>{SIDEBAR_INIT_SCRIPT}</ScriptOnce>

      {/* Palette values for the ids on <html>, shared with the content layout.
            This stylesheet unmounts with the shell. */}
      <style dangerouslySetInnerHTML={{ __html: PALETTE_CSS }} />

      {/* ── Top bar: brand + apps, search, account ── */}
      {/* On desktop the bar's side padding is set so its end controls sit on the rails' center
          lines: 12px matches the sidebar's own padding; 8px + the account button's 4px + half
          the 32px avatar = 28px, the center of the 56px tools rail. */}
      <header className="relative z-20 flex h-14 shrink-0 items-center gap-3 px-3 sm:px-4 lg:pr-2 lg:pl-3">
        <div
          className={cn(
            // Header padding (12) + this width + the gap (12) = the sidebar width (248, or 56 as a
            // rail), so search lines up with the panel and the grid button centers over the rail icons.
            "hb-sidebar-scope flex shrink-0 items-center gap-1 lg:w-[224px] lg:justify-between lg:transition-[width] lg:duration-200 lg:ease-out",
            "lg:sidebar-rail:w-8 lg:sidebar-rail:justify-center",
          )}
        >
          <Tooltip content="Open navigation" side="bottom" wrapperClassName="lg:hidden">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              shape="sharp"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </Tooltip>

          <Link
            to={INBOX_PATH}
            search={{ domainId: undefined, aliasId: undefined, direction: "inbound", q: undefined }}
            aria-label="Bavimail inbox"
            className={cn(
              "hidden items-center pl-2 transition-opacity hover:opacity-80 sm:flex",
              "lg:sidebar-rail:hidden",
            )}
          >
            <BrandLogo className="h-[18px] w-auto" />
          </Link>

          <AppsMenu pathname={pathname} />
        </div>

        <MailSearchField className="min-w-0 flex-1 lg:max-w-[560px]" />

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <ThemeToggle className="hidden sm:inline-flex" />
          <AccountMenu isSigningOut={isSigningOut} onSignOut={() => void handleSignOut()} />
        </div>
      </header>

      {/* ── Mobile nav drawer ── */}
      <Drawer
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        side="left"
        ariaLabel="Navigation menu"
        className="w-[288px] bg-hb-sidebar lg:hidden"
        backdropClassName="lg:hidden"
      >
        <div className="flex items-center justify-between px-4 py-4">
          <BrandLogo className="h-[18px] w-auto" />
          <Tooltip content="Close navigation" side="bottom">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              shape="sharp"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close navigation"
            >
              <X className="h-5 w-5" />
            </Button>
          </Tooltip>
        </div>

        <nav aria-label="Main navigation" className="flex-1 overflow-y-auto px-3 pb-4">
          <MailNav expanded />

          <SectionLabel className="mt-6">Tools</SectionLabel>
          <div className="space-y-0.5">
            {DASHBOARD_FEATURES.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActivePath(pathname, item.to) ? "page" : undefined}
                className={navItemClassName(isActivePath(pathname, item.to))}
              >
                <item.icon
                  aria-hidden="true"
                  className={cn(
                    "h-4 w-4 shrink-0",
                    isActivePath(pathname, item.to) ? "text-hb-heading" : "text-hb-muted",
                  )}
                />
                <span className="truncate text-[13px]">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        <div className="flex items-center justify-between border-t border-hb-border px-4 py-3">
          <span className="text-xs text-hb-faint">Theme</span>
          <ThemeToggle />
        </div>
      </Drawer>

      <div className="relative z-10 flex min-h-0 flex-1">
        {/* ── Left sidebar: compose + folders ── visible ≥ lg ── */}
        {/* As a rail it matches the 56px tools rail, so the panel sits evenly between them. */}
        <aside
          aria-label="Mailbox sidebar"
          className={cn(
            "hb-sidebar-scope hidden w-[248px] shrink-0 flex-col overflow-hidden px-3 pb-3 transition-[width,padding] duration-200 ease-out lg:flex",
            "sidebar-rail:w-14 sidebar-rail:px-2",
          )}
          style={{ contain: "layout style" }}
        >
          <nav
            aria-label="Main navigation"
            className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
          >
            <MailNav expanded={sidebarPinned} />
          </nav>

          <div
            className={cn(
              // justify-end while the Agent access link is commented out (it was justify-between).
              "mt-2 flex items-center justify-end gap-1",
              "sidebar-rail:flex-col-reverse sidebar-rail:justify-center",
            )}
          >
            {/* Agent access link: disabled for now, will be re-enabled. To restore, uncomment this
                block and the `Bot` import, and set the row back to `justify-between`.
            <Tooltip
              content="Agent access: API and MCP docs"
              side="right"
              wrapperClassName="flex min-w-0 flex-1 sidebar-rail:flex-none"
            >
              <Link
                to="/docs/mcp"
                aria-label="Agent access: API and MCP docs"
                className={cn(
                  "flex h-8 min-w-0 flex-1 items-center gap-2.5 rounded-md px-3 text-[13px] text-hb-muted transition-colors hover:bg-hb-raised/60 hover:text-hb-heading",
                  "sidebar-rail:w-8 sidebar-rail:flex-none sidebar-rail:justify-center sidebar-rail:px-0",
                )}
              >
                <Bot aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span className="truncate sidebar-rail:hidden">Agent access</span>
              </Link>
            </Tooltip>
            */}
            <Tooltip
              content={`${sidebarPinned ? "Collapse sidebar" : "Expand sidebar"} (Ctrl+[)`}
              side="right"
            >
              <Button
                variant="ghost"
                size="icon"
                shape="sharp"
                onClick={toggleSidebarPin}
                aria-expanded={sidebarPinned}
                aria-label={sidebarPinned ? "Collapse sidebar" : "Expand sidebar"}
              >
                <PanelLeftClose className="h-4 w-4 sidebar-rail:hidden" />
                <PanelLeftOpen className="hidden h-4 w-4 sidebar-rail:block" />
              </Button>
            </Tooltip>
          </div>
        </aside>

        {/* ── Content panel ── */}
        <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden border-hb-border bg-hb-page max-lg:border-t lg:mb-3 lg:rounded-[10px] lg:border">
          {children}
        </main>

        {/* ── Right rail: the same tools as the apps menu ── visible ≥ lg ── */}
        <aside
          aria-label="Tools"
          // pt-0.5: the 36px icons share a center line with the 40px Compose button across the panel.
          className="hidden w-14 shrink-0 flex-col items-center gap-1 pt-0.5 pb-3 lg:flex"
        >
          {DASHBOARD_FEATURES.map((item) => (
            <RailLink
              key={item.to}
              active={isActivePath(pathname, item.to)}
              icon={item.icon}
              label={item.label}
              to={item.to}
            />
          ))}
        </aside>
      </div>

      <Modal open={Boolean(signOutError)} onOpenChange={() => {}}>
        <ModalContent
          role="alertdialog"
          ariaLabel="Sign-out needs another try"
          ariaDescribedBy="signout-error-desc"
          dismissible={false}
          showCloseButton={false}
          size="sm"
          className="border-hb-danger-border"
        >
          <div className="p-8 text-center md:p-10">
            <div
              aria-hidden="true"
              className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-hb-danger-border bg-hb-danger-surface text-hb-danger-text"
            >
              <LogOut className="h-5 w-5" />
            </div>
            <h2 className="mb-2 text-xl font-extrabold tracking-tight text-hb-heading">
              Sign-out needs another try
            </h2>
            <p id="signout-error-desc" className="text-sm leading-relaxed text-hb-danger-text">
              {signOutError ?? lastSignOutError}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <StatefulButton
                size="lg"
                type="button"
                state={isSigningOut ? "loading" : "idle"}
                loadingText="Retrying"
                onClick={() => void handleSignOut()}
              >
                Retry sign out
              </StatefulButton>
              <Button
                size="lg"
                type="button"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Reload app
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
}

function navItemClassName(active: boolean) {
  return cn(
    "group relative flex h-9 w-full items-center gap-3 rounded-md px-3 text-left transition-colors duration-[var(--motion-duration-fast)]",
    active
      ? "bg-hb-raised font-semibold text-hb-heading"
      : "font-medium text-hb-text hover:bg-hb-raised/60 hover:text-hb-heading",
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("mb-1.5 px-3 text-xs font-medium text-hb-faint", className)}>{children}</div>
  );
}

/** Compose button + mailbox folders, shared by the desktop sidebar and the mobile drawer. */
function MailNav({ expanded }: { expanded: boolean }) {
  const { onMail, mail } = useMailLocation();

  return (
    <>
      <Tooltip
        content="Compose"
        side="right"
        open={expanded ? false : undefined}
        wrapperClassName="flex w-full"
      >
        <ButtonRouterLink
          to={COMPOSE_PATH}
          search={{
            domainId: mail.domainId,
            aliasId: mail.aliasId,
            direction: mail.direction,
            q: mail.q,
          }}
          preload="intent"
          preloadDelay={50}
          aria-label="Compose"
          hoverScale={1}
          shape="sharp"
          className="h-10 w-full rounded-lg text-sm font-semibold sidebar-rail:px-0"
        >
          <PenLine aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="sidebar-rail:hidden">Compose</span>
        </ButtonRouterLink>
      </Tooltip>

      <div className="mt-3 space-y-px">
        {DASHBOARD_FOLDERS.map((folder) => {
          const active = onMail && mail.direction === folder.direction;

          return (
            <Tooltip
              key={folder.direction}
              content={folder.label}
              side="right"
              open={expanded ? false : undefined}
              wrapperClassName="flex w-full"
            >
              <Link
                to={INBOX_PATH}
                search={{
                  domainId: mail.domainId,
                  aliasId: mail.aliasId,
                  direction: folder.direction,
                  q: undefined,
                  includeBounce: mail.includeBounce,
                  includeWarmup: mail.includeWarmup,
                }}
                preload="intent"
                preloadDelay={60}
                aria-label={expanded ? undefined : folder.label}
                aria-current={active ? "page" : undefined}
                className={cn(
                  navItemClassName(active),
                  // The same 36px square as the tools rail links.
                  "sidebar-rail:mx-auto sidebar-rail:w-9 sidebar-rail:justify-center sidebar-rail:px-0",
                )}
              >
                <folder.icon
                  aria-hidden="true"
                  className={cn("h-4 w-4 shrink-0", active ? "text-hb-heading" : "text-hb-muted")}
                />
                <span className="truncate text-[13px] sidebar-rail:hidden">{folder.label}</span>
              </Link>
            </Tooltip>
          );
        })}
      </div>
    </>
  );
}

/** Top-bar search. Filters the open mailbox as you type; from anywhere else, Enter opens it. */
function MailSearchField({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { pathname, mail } = useMailLocation();
  const onList = pathname.replace(/\/$/, "") === INBOX_PATH;
  const routeQuery = mail.q ?? "";
  const [value, setValue] = useState(routeQuery);
  const lastRouteQueryRef = useRef(routeQuery);
  const inputRef = useRef<HTMLInputElement>(null);

  // Follow the URL (back/forward, folder switch) unless the user is mid-edit.
  useEffect(() => {
    if (value === lastRouteQueryRef.current) {
      setValue(routeQuery);
    }
    lastRouteQueryRef.current = routeQuery;
  }, [routeQuery, value]);

  const runSearch = useCallback(
    (raw: string, replace: boolean) => {
      void navigate({
        to: INBOX_PATH,
        replace,
        search: {
          domainId: mail.domainId,
          aliasId: mail.aliasId,
          direction: mail.direction,
          q: raw.trim() || undefined,
          includeBounce: mail.includeBounce,
          includeWarmup: mail.includeWarmup,
        },
      });
    },
    [mail.aliasId, mail.direction, mail.domainId, mail.includeBounce, mail.includeWarmup, navigate],
  );

  useEffect(() => {
    if (!onList) return;
    if ((value.trim() || undefined) === mail.q) return;

    const timeout = window.setTimeout(() => runSearch(value, true), 200);
    return () => window.clearTimeout(timeout);
  }, [mail.q, onList, runSearch, value]);

  // "/" jumps to search, the way every mail client does it.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      event.preventDefault();
      inputRef.current?.focus();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    runSearch(value, onList);
  };

  return (
    <form role="search" onSubmit={handleSubmit} className={className}>
      <Input
        ref={inputRef}
        type="search"
        size="sm"
        value={value}
        onChange={setValue}
        placeholder="Search mail"
        aria-label="Search mail"
        aria-keyshortcuts="/"
        leftIcon={<Search aria-hidden="true" />}
        rightIcon={
          <kbd className="hidden rounded border border-hb-card px-1.5 font-hb text-[11px] leading-[18px] text-hb-faint sm:block">
            /
          </kbd>
        }
        classNames={{
          field: "h-9 rounded-lg border-hb-border bg-hb-surface",
          input: "font-hb text-[13px]",
          leftIcon: "text-hb-muted [&_svg]:h-4 [&_svg]:w-4",
        }}
      />
    </form>
  );
}

/** The grid button beside the logo: every non-mailbox tool, as tiles. */
function AppsMenu({ pathname }: { pathname: string }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <MorphPopover open={open} onOpenChange={setOpen}>
      <MorphPopoverTrigger>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          shape="sharp"
          aria-label="Open tools menu"
        >
          <LayoutGrid className="h-[18px] w-[18px]" />
        </Button>
      </MorphPopoverTrigger>
      <MorphPopoverContent
        align="start"
        radius={10}
        ariaLabel="Tools"
        className="w-[296px] border-solid p-2"
      >
        <div className="grid grid-cols-3 gap-1">
          <AppTile
            active={isMailPath(pathname)}
            icon={Mail}
            label="Mail"
            to={INBOX_PATH}
            search={{ domainId: undefined, aliasId: undefined, direction: "inbound", q: undefined }}
          />
          {DASHBOARD_FEATURES.map((item) => (
            <AppTile
              key={item.to}
              active={isActivePath(pathname, item.to)}
              icon={item.icon}
              label={item.label}
              to={item.to}
            />
          ))}
        </div>
      </MorphPopoverContent>
    </MorphPopover>
  );
}

function AppTile({
  active,
  icon: Icon,
  label,
  to,
  search,
}: {
  active: boolean;
  icon: LucideIcon;
  label: string;
  to: string;
  search?: Record<string, unknown>;
}) {
  return (
    <Link
      to={to}
      search={search}
      aria-current={active ? "page" : undefined}
      className="group flex flex-col items-center gap-2 rounded-lg px-1 py-3 text-center transition-colors hover:bg-hb-raised"
    >
      <span
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-lg border transition-colors",
          active
            ? "border-hb-border-hover bg-hb-raised text-hb-heading"
            : "border-hb-card bg-hb-inset text-hb-muted group-hover:border-hb-border-hover group-hover:text-hb-heading",
        )}
      >
        <Icon aria-hidden="true" className="h-[18px] w-[18px]" />
      </span>
      <span
        className={cn(
          "text-xs leading-tight",
          active ? "font-semibold text-hb-heading" : "text-hb-muted group-hover:text-hb-heading",
        )}
      >
        {label}
      </span>
    </Link>
  );
}

function RailLink({
  active,
  icon: Icon,
  label,
  to,
}: {
  active: boolean;
  icon: LucideIcon;
  label: string;
  to: string;
}) {
  return (
    <Tooltip content={label} side="left">
      <Link
        to={to}
        preload="viewport"
        preloadDelay={60}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className={cn(
          "relative flex h-9 w-9 items-center justify-center rounded-md transition-colors duration-[var(--motion-duration-fast)]",
          active
            ? "bg-hb-raised text-hb-heading"
            : "text-hb-muted hover:bg-hb-raised/60 hover:text-hb-heading",
        )}
      >
        <Icon aria-hidden="true" className="h-4 w-4" />
      </Link>
    </Tooltip>
  );
}

function AccountMenu({
  isSigningOut,
  onSignOut,
}: {
  isSigningOut: boolean;
  onSignOut: () => void;
}) {
  const { user } = useUser();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [open, setOpen] = useState(false);
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const name = user?.fullName || email.split("@")[0] || "Account";

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const avatar = (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-hb-raised text-xs font-semibold text-hb-heading">
      {user?.hasImage ? (
        <img src={user.imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        getInitials(name)
      )}
    </span>
  );

  return (
    <MorphPopover open={open} onOpenChange={setOpen}>
      <MorphPopoverTrigger>
        <button
          type="button"
          aria-label="Account menu"
          className="motion-press flex items-center gap-2.5 rounded-full py-1 pl-1 pr-1 text-left transition-colors hover:bg-hb-raised/60 xl:pl-3"
        >
          <span className="hidden min-w-0 text-right xl:block">
            <span className="block max-w-[180px] truncate text-[13px] font-semibold leading-tight text-hb-heading">
              {name}
            </span>
            <span className="block max-w-[180px] truncate text-xs leading-tight text-hb-muted">
              {email}
            </span>
          </span>
          {avatar}
        </button>
      </MorphPopoverTrigger>
      <MorphPopoverContent radius={10} ariaLabel="Account" className="w-[272px] border-solid p-2">
        <div className="flex items-center gap-3 px-2 pb-3 pt-2">
          {avatar}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-hb-heading">{name}</div>
            <div className="truncate text-xs text-hb-muted">{email}</div>
          </div>
        </div>
        <div className="border-t border-hb-border pt-2">
          <Link
            to="/dashboard/settings"
            search={{ tab: "profile" }}
            className={navItemClassName(false)}
          >
            <Settings aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="text-sm">Settings</span>
          </Link>
          <button
            type="button"
            onClick={onSignOut}
            disabled={isSigningOut}
            className={cn(navItemClassName(false), "disabled:opacity-50")}
          >
            <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="text-sm">{isSigningOut ? "Signing out..." : "Sign out"}</span>
          </button>
        </div>
      </MorphPopoverContent>
    </MorphPopover>
  );
}

export function DashboardPlaceholderPage({
  description,
  icon: Icon,
  title,
}: {
  description: string;
  icon: LucideIcon;
  title: string;
}) {
  return (
    <>
      <div className="flex items-center gap-3 border-b border-hb-border px-4 py-4 sm:px-6">
        <Icon className="h-5 w-5 text-hb-accent" />
        <h1 className="font-hb text-lg font-semibold tracking-tight text-hb-heading">{title}</h1>
      </div>

      <EmptyStateCard
        icon={<Icon className="h-5 w-5 text-hb-faint" />}
        heading={`${title} coming soon`}
        description={description}
      />
    </>
  );
}
